const ensure = require('Assert');

cc.Class({
    extends: cc.Component,

    properties: {
        maxMusicSimultaneously:
        {
            type: cc.Integer,
            min: 1,
            max: 10,
            step: 1,
            default: 1
        }

    },

    ctor () {
        this.musicVolume = 1;
        this.sfxVolume = 1;
        this.volume = 1; // global volume
        this.musicEnabled = true;
        this.sfxEnabled = true;
        this.musicMap = new Map();
        this.sfxMap = new Map();
    },

    onLoad () {
        cc.game.addPersistRootNode(this.node);
        this._resetMusicMap();
    },

    _resetMusicMap () {
        this.musicMap.clear();
        for (let i = 0; i < this.maxMusicSimultaneously; i++) {
            this.musicMap.set(i.toString(), -1);
        }
    },

    /**
     * Setting up the global volume
     * @param {Number} value between 0.0 ~ 1.0
     */
    setGlobalVolume (value) {
        value /= 10;
        if (ensure.numberRange(value, 0, 1)) {
            this.volume = value;
            this.updateVolume();
        }
    },

    /**
     * return global volume value (0.0 ~ 1.0)
     */
    getGlobalVolume () {
        return this.volume;
    },

    /**
     * Update sound volumes for both music and sfx sounds
     */
    updateVolume () {
        const effectValue = (this.sfxVolume * this.volume).toString().substr(0, 4);

        cc.audioEngine.setEffectsVolume(effectValue);

        const musicValue = (this.musicVolume * this.volume).toString().substr(0, 4);

        this.musicMap.forEach((v) => {
            cc.audioEngine.setVolume(v.id, musicValue);
        });
    },

    //////////////////// BACKGROUND MUSIC ///////////////////////
    /**
     * Sets only music's volume (won't affect on sfx volume),
     * @param {Number} volume - the float value between 0.0 ~ 1.0
     */
    setMusicVolume (volume) {
        volume /= 10;
        if (ensure.numberRange(volume, 0, 1)) {
            this.musicVolume = volume;
            this.updateVolume();
        }
    },

    /**
     *  return music's volume
     */
    getMusicVolume () {
        return this.musicVolume;
    },

    /**
     * set music enabled, stop all musics if the value is false
     * @param {boolean} value
     */
    setMusicEnabled (value) {
        if (value && value !== this.musicEnabled) {
            cc.audioEngine.resume(this.musicMap[0]);
        }
        this.musicEnabled = value;
        if (!value) {
            this.stopAllMusic();
        }
    },

    /**
     * return true if music is enabled
     */
    getMusicEnabled () {
        return this.musicEnabled;
    },

    /**
     * Play given music on given track, only one music can be played in each track
     * @param {Number} audio
     * @param {boolean} loop
     * @param {Number} trackIndex - track index, only one music can be played in each track
     * @returns {Number} audio id of the music played
     */
    playMusic (audio, loop = true, trackIndex = 0, onAudioEndCallback = null) {
        let idx = -1;

        if (this.musicEnabled && audio && this.musicVolume && this.volume) {
            this.stopMusic(trackIndex, false);
            const musicValue = (this.musicVolume * this.volume).toString().substr(0, 4);

            idx = cc.audioEngine.play(audio, loop, musicValue);

            this.musicMap.set(trackIndex.toString(), {
                // TODO, for review not used for now. maybe in the future
                id: idx,
                cb: onAudioEndCallback
            });

            cc.audioEngine.setFinishCallback(idx, () => {
                if (onAudioEndCallback !== null) {
                    onAudioEndCallback();
                }

                this.musicMap.set(trackIndex.toString, -1);
            });
        }

        return idx;
    },

    /**
     * Stop music on a given track
     * @param {Number} trackIndex track index to be stopped
     * @param {boolean} check if true, do not check/ensure/validate track index
     */
    stopMusic (trackIndex = 0, check = true, isExecuteCallback = true) {
        if (!check || ensure.numberLower(trackIndex, this.maxMusicSimultaneously)) {
            if (this.musicMap[trackIndex] !== -1) {
                cc.audioEngine.stop(this.musicMap[trackIndex]);

                const elem = this.musicMap.get(trackIndex.toString());

                if (elem.cb !== null && isExecuteCallback) {
                    elem.cb();
                }

                this.musicMap.set(trackIndex.toString, -1);
            }

        }
    },

    /**
     * Stop all musics and clear track indicies
     */
    stopAllMusic () {
        cc.audioEngine.pauseAll();
    },

    ////////////////////// SOUND EFFECTS //////////////////////////
    /**
     * Sets only sfx's volume (won't affect on music volume),
     * @param {Number} volume - the float value between 0.0 ~ 1.0
     */
    setSfxVolume (volume) {
        volume /= 10;
        if (ensure.numberRange(volume, 0, 1)) {
            this.sfxVolume = volume;
            this.updateVolume();
        }
    },

    /**
     * return sfx volume, number between 0.0 ~ 1.0
     */
    getSfxVolume () {
        return this.sfxVolume;
    },

    /**
     * set sfx enabled, stop all sfx sounds if the value is false
     * @param {boolean} value
     */
    setSfxEnabled (value) {
        this.sfxEnabled = value;
        if (!value) {
            this.stopAllSfx();
        }
    },

    // check if sfx is enabled
    getSfxEnabled () {
        return this.sfxEnabled;
    },

    /**
     * Play given audio if sfxSounds are enabled
     * @param {cc.audioClip} audioClip to be played
     * @param {Boolean} loop to be played
     * @param {function} onAudioEndCallback for when the sfx has stopped playing
     * @returns {number} audio ID for the played sfx
     */
    playSfx (audioClip, loop = false, onAudioEndCallback = null) {
        if (audioClip === null) {
            cc.log('null object was passed');

            return;
        }

        if (this.sfxEnabled && this.volume && this.sfxVolume) {
            const audioId = cc.audioEngine.playEffect(audioClip, loop);

            this.sfxMap.set(audioId.toString(), {
                audio: audioClip,
                cb: onAudioEndCallback
            });

            // TODO check for performance issue
            cc.audioEngine.setFinishCallback(audioId, () => {
                const elem = this.sfxMap.get(audioId.toString());

                if (elem.cb !== null) {
                    elem.cb();
                }

                this.sfxMap.delete(audioId);
                // cc.log(`audio ${audioId} has ended`);
            });

            return audioId;
        }
    },

    /**
     * stop specified audio and call callbacks if available
     * @param {number|cc.audioClip|string} parameter for audio clip to be stopped.
     * will look into it's internal map to see if the audio
     * @param {boolean} if true, will call the callback assigned if it is available
     */
    stopSfx (args, isExecuteCallback = true) {
        if (args instanceof cc.AudioClip) {
            const indices = [];

            this.sfxMap.forEach((v, k) => {
                if (v.audio === args) {
                    indices.push(Number(k));
                    cc.audioEngine.stopEffect(k);

                    if (v.cb !== null && isExecuteCallback) {
                        v.cb();
                    }
                }
            });

            indices.forEach((elem) => {
                this.sfxMap.delete(elem.toString());
            });
        } else if (args !== null) {
            const item = this.sfxMap.get(args.toString());

            if (item !== null) {
                cc.audioEngine.stopEffect(args);

                if (item.cb !== null && isExecuteCallback) {
                    item.cb();
                }

                this.sfxMap.delete(args.toString());
            }
        } else {
            cc.log('Invalid param value');
        }
    },

    /**
     * stop all playing sfx sounds
     * @param {boolean} if true it will play callbacks for sfx if needed
     */
    stopAllSfx (isExecuteCallback = true) {
        cc.audioEngine.stopAllEffects();
        if (isExecuteCallback) {
            this.sfxMap.forEach((v) => {
                if (v.cb !== null) {
                    v.cb();
                }
            });
        }

        this.sfxMap.clear();
    }
});
